import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Globe as GlobeIcon, Package, Users, Building2 } from 'lucide-react';
import GlobeGL from 'react-globe.gl';
import qualityImage from '../assets/q.png';

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const CountUp = ({ end, duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setStarted(true); obs.disconnect(); }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let start = 0;
    const steps = duration / 16;
    const inc = end / steps;
    const timer = setInterval(() => {
      start += inc;
      if (start >= end) { clearInterval(timer); setCount(end); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, end, duration]);
  return <span ref={ref}>{count}</span>;
};

const statIcons = [GlobeIcon, Package, Users, Building2];

const regionColors = {
  'Asia': '#3a8fa3', 'América': '#5aab9a', 'Americas': '#5aab9a',
  'Europa': '#4a9db5', 'Europe': '#4a9db5',
  'África': '#2d7a8e', 'Africa': '#2d7a8e',
  'Medio Oriente': '#6bbfad', 'Middle East': '#6bbfad',
  'Oceanía': '#4db8a0', 'Oceania': '#4db8a0',
};

// Rich country details: flag, capital, partnership, since, focus
const countryDetails = {
  'India': {
    flag: 'https://flagcdn.com/w40/in.png',
    capital:'New Delhi',
    partnership:'Headquarters',
    since:'2022',
    focus:'Manufacturing & R&D',
    marketSize:'Large',
    status:'Active'
  },
  'Guatemala': {
    flag:'https://flagcdn.com/w40/gt.png',
    capital:'Guatemala City',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions & Injections',
    marketSize:'Medium',
    status:'Growing'
  },
  'Honduras': {
    flag:'https://flagcdn.com/w40/hn.png',
    capital:'Tegucigalpa',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions',
    marketSize:'Medium',
    status:'Growing'
  },
  'Nicaragua': {
    flag:'https://flagcdn.com/w40/ni.png',
    capital:'Managua',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions & Injections',
    marketSize:'Medium',
    status:'Growing'
  },
  'Costa Rica': {
    flag:'https://flagcdn.com/w40/cr.png',
    capital:'San Jose',
    partnership:'Export Market',
    since:'2024',
    focus:'Injections',
    marketSize:'Medium',
    status:'Growing'
  },
  'Panama': {
    flag:'https://flagcdn.com/w40/pa.png',
    capital:'Panama City',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions',
    marketSize:'Medium',
    status:'Growing'
  },
  'Dominican Republic': {
    flag:'https://flagcdn.com/w40/do.png',
    capital:'Santo Domingo',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions & Injections',
    marketSize:'Medium',
    status:'Growing'
  },
  'Venezuela': {
    flag:'https://flagcdn.com/w40/ve.png',
    capital:'Caracas',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions',
    marketSize:'Medium',
    status:'Growing'
  },
  'Ecuador': {
    flag:'https://flagcdn.com/w40/ec.png',
    capital:'Quito',
    partnership:'Export Market',
    since:'2024',
    focus:'Injections',
    marketSize:'Medium',
    status:'Growing'
  },
  'Bolivia': {
    flag:'https://flagcdn.com/w40/bo.png',
    capital:'Sucre',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions & Injections',
    marketSize:'Medium',
    status:'Growing'
  },
  'Peru': {
    flag:'https://flagcdn.com/w40/pe.png',
    capital:'Lima',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions',
    marketSize:'Medium',
    status:'Growing'
  },
  'Chile': {
    flag:'https://flagcdn.com/w40/cl.png',
    capital:'Santiago',
    partnership:'Export Market',
    since:'2024',
    focus:'Oncology',
    marketSize:'Medium',
    status:'Growing'
  },
  'Cuba': {
    flag:'https://flagcdn.com/w40/cu.png',
    capital:'Havana',
    partnership:'Export Market',
    since:'2024',
    focus:'Infusions & Injections',
    marketSize:'Medium',
    status:'Growing'
  }
};
const countryCoords = {
  'India':       { lat: 26.5937,  lng: 75.9629  },
  // 'USA':         { lat: 37.0902,  lng: -95.7129 },
  // 'EE.UU.':      { lat: 37.0902,  lng: -95.7129 },
  // 'Germany':     { lat: 51.1657,  lng: 10.4515  },
  // 'Alemania':    { lat: 51.1657,  lng: 10.4515  },
  // 'Brazil':      { lat: -14.235,  lng: -51.9253 },
  // 'Brasil':      { lat: -14.235,  lng: -51.9253 },
  // 'Nigeria':     { lat: 9.082,    lng: 8.6753   },
  // 'UAE':         { lat: 23.4241,  lng: 53.8478  },
  // 'EAU':         { lat: 23.4241,  lng: 53.8478  },
  // 'Philippines': { lat: 12.8797,  lng: 121.774  },
  // 'Filipinas':   { lat: 12.8797,  lng: 121.774  },
  // 'Vietnam':     { lat: 14.0583,  lng: 108.2772 },
  // 'Kenya':       { lat: -0.0236,  lng: 37.9062  },
  // 'Kenia':       { lat: -0.0236,  lng: 37.9062  },
  // 'Mexico':      { lat: 23.6345,  lng: -102.5528},
  // 'México':      { lat: 23.6345,  lng: -102.5528},
  // 'UK':          { lat: 55.3781,  lng: -3.436   },
  // 'Reino Unido': { lat: 55.3781,  lng: -3.436   },
  // 'Australia':   { lat: -25.2744, lng: 133.7751 },
  'Guatemala':          { lat: 15.7835,  lng: -90.2308  },
  'Honduras':           { lat: 15.2000,  lng: -86.2419  },
  'Nicaragua':          { lat: 12.8654,  lng: -85.2072  },
  'Costa Rica':         { lat: 9.7489,   lng: -83.7534  },
  'Panama':             { lat: 8.5380,   lng: -80.7821  },
  'Dominican Republic': { lat: 18.7357,  lng: -70.1627  },
  'Venezuela':          { lat: 6.4238,   lng: -66.5897  },
  'Ecuador':            { lat: -1.8312,  lng: -78.1834  },
  'Bolivia':            { lat: -16.2902, lng: -63.5887  },
  'Peru':               { lat: -9.1900,  lng: -75.0152  },
  'Chile':              { lat: -35.6751, lng: -71.5430  },
  'Cuba':               { lat: 21.5218,  lng: -77.7812  },
};

const GlobalPresence = () => {
  const { t } = useTranslation();
  const [activeCountry, setActiveCountry] = useState(null);
  const [tooltipPos, setTooltipPos]       = useState({ x: 0, y: 0 });
  const globeRef      = useRef(null);
  const wrapRef       = useRef(null);
  const hoverTimeout  = useRef(null);

  const [heroRef,   heroVis]   = useReveal(0);
  const [statsRef,  statsVis]  = useReveal(0);
  const [mapSecRef, mapSecVis] = useReveal(0);
  const [listRef,   listVis]   = useReveal(0);
  const [ctaRef,    ctaVis]    = useReveal(0);

  // Pull from translations
  const rawCountries = t('globalPage.countries', { returnObjects: true }) || [];

  const latinAmericaExtra = [
    { name: 'Guatemala',          region: 'Americas', type: 'Export', products: ['Infusions','Injections'] },
    { name: 'Honduras',           region: 'Americas', type: 'Export', products: ['Infusions'] },
    { name: 'Nicaragua',          region: 'Americas', type: 'Export', products: ['Infusions','Injections'] },
    { name: 'Costa Rica',         region: 'Americas', type: 'Export', products: ['Injections'] },
    { name: 'Panama',             region: 'Americas', type: 'Export', products: ['Infusions'] },
    { name: 'Dominican Republic', region: 'Americas', type: 'Export', products: ['Infusions','Injections'] },
    { name: 'Venezuela',          region: 'Americas', type: 'Export', products: ['Infusions'] },
    { name: 'Ecuador',            region: 'Americas', type: 'Export', products: ['Injections'] },
    { name: 'Bolivia',            region: 'Americas', type: 'Export', products: ['Infusions','Injections'] },
    { name: 'Peru',               region: 'Americas', type: 'Export', products: ['Infusions'] },
    { name: 'Chile',              region: 'Americas', type: 'Export', products: ['Oncology'] },
    { name: 'Cuba',               region: 'Americas', type: 'Export', products: ['Infusions','Injections'] },
  ];
  const existingNames = new Set(rawCountries.map(c => c.name));
  const mergedRaw = [...rawCountries, ...latinAmericaExtra.filter(c => !existingNames.has(c.name))];

  const countries = mergedRaw.map(c => ({
    ...c,
    ...(countryDetails[c.name] || {}),
    lat: countryCoords[c.name]?.lat ?? 0,
    lng: countryCoords[c.name]?.lng ?? 0,
  }));

  const hqCountry = countries.find(c => c.type === 'HQ');

  // Arc data: HQ → every export country
  const arcs = hqCountry
    ? countries
        .filter(c => c.type !== 'HQ')
        .map(c => ({
          startLat: hqCountry.lat, startLng: hqCountry.lng,
          endLat:   c.lat,         endLng:   c.lng,
          color:    regionColors[c.region] || '#3a8fa3',
          country:  c,
        }))
    : [];

  const rawStats = t('globalPage.stats', { returnObjects: true }) || [];
  const statVals   = [12, 150, 50];
  const stats = rawStats.map((s, i) => ({ val: statVals[i], suffix: '+', label: s.label, icon: statIcons[i] }));

  // Auto-rotate: pause on hover, resume on leave
  useEffect(() => {
    if (!globeRef.current) return;
    const ctrl = globeRef.current.controls();
    ctrl.autoRotate      = true;
    ctrl.autoRotateSpeed = 0.4;
    ctrl.enableZoom      = true;
    ctrl.minDistance     = 200;
    ctrl.maxDistance     = 600;
    globeRef.current.pointOfView({ lat: 20, lng: 20, altitude: 2.0 }, 0);
  }, [mapSecVis]);

  // Debounced point hover — prevents 1-frame flicker when cursor crosses point boundary
  const handlePointHover = useCallback((point) => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    if (point) {
      setActiveCountry(point);
    } else {
      hoverTimeout.current = setTimeout(() => setActiveCountry(null), 1000);
    }
  }, []);

  // Track mouse for tooltip position
  const handleGlobeMouseMove = useCallback((e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  return (
    <div className="gp-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes gpHeroIn  { from{opacity:0;transform:translateY(36px)} to{opacity:1;transform:translateY(0)} }
        @keyframes gpSlideL  { from{opacity:0;transform:translateX(-40px)} to{opacity:1;transform:translateX(0)} }
        @keyframes gpSlideR  { from{opacity:0;transform:translateX(40px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes gpUp      { from{opacity:0;transform:translateY(32px)}  to{opacity:1;transform:translateY(0)} }
        @keyframes gpPing    { 0%{transform:scale(1);opacity:0.8} 70%{transform:scale(2.5);opacity:0} 100%{transform:scale(2.5);opacity:0} }
        @keyframes gpPulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
        @keyframes gpFloat   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }

        .gp-root { font-family:'Outfit',sans-serif; }

        /* ══ HERO ══ */
        .gp-hero {
          position:relative; min-height:280px; display:flex; align-items:center;
          overflow:hidden; padding-top:80px;
          background:linear-gradient(135deg,#0d2233 0%,#1a3a4a 55%,#0f2d3d 100%);
        }
        .gp-hero-bg { position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.15; }
        .gp-hero-gridlines {
          position:absolute;inset:0;pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:56px 56px;
        }
        .gp-hero-topline {
          position:absolute;top:0;left:0;right:0;height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,#3a8fa3,transparent);
        }
        .gp-hero-inner {
          position:relative;z-index:2;max-width:1280px;margin:0 auto;
          padding:70px 32px 64px;width:100%;
          display:flex;flex-direction:column;align-items:center;text-align:center;
        }
        .gp-hero-eyebrow {
          display:inline-block;font-size:0.72rem;font-weight:600;
          letter-spacing:0.14em;text-transform:uppercase;
          color:#5aab9a;background:rgba(90,171,154,0.12);
          border:1px solid rgba(90,171,154,0.3);
          padding:5px 16px;border-radius:999px;margin-bottom:18px;
        }
        .gp-hero-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(3rem,7vw,5.5rem);font-weight:800;
          color:#fff;margin:0 0 16px;line-height:1.1;
          text-shadow:0 2px 24px rgba(0,0,0,0.3);
        }
        .gp-hero-sub {
          font-size:0.85rem;font-weight:500;letter-spacing:0.1em;
          color:rgba(255,255,255,0.4);text-transform:uppercase;margin-top:0;
        }
        .gp-hero-anim { animation:gpHeroIn 0.85s cubic-bezier(0.4,0,0.2,1) both; }
        .gp-hero-anim-r { animation:gpHeroIn 0.85s cubic-bezier(0.4,0,0.2,1) 0.2s both; }

        /* ══ Shared BG ══ */
        .gp-teal   { background:linear-gradient(180deg,#e8f5f7 0%,#d4eef2 60%,#e0f4f7 100%); position:relative;overflow:hidden; }
        .gp-teal-2 { background:linear-gradient(180deg,#d4eef2 0%,#c8e8ed 100%); position:relative;overflow:hidden; }
        .gp-dots {
          position:absolute;inset:0;pointer-events:none;
          background-image:radial-gradient(circle,rgba(58,143,163,0.06) 1px,transparent 1px);
          background-size:36px 36px;
        }
        .gp-blob { position:absolute;border-radius:50%;pointer-events:none;filter:blur(80px); }
        .gp-blob-tl { width:400px;height:400px;background:rgba(58,143,163,0.08);top:-100px;left:-100px; }
        .gp-blob-br { width:350px;height:350px;background:rgba(90,171,154,0.07);bottom:-80px;right:-80px; }
        .gp-inner { max-width:1280px;margin:0 auto;padding:0 32px;position:relative;z-index:1; }

        /* ══ STATS ══ */
        .gp-stats { padding:60px 0; }
        .gp-stats-grid {
          display:grid;grid-template-columns:repeat(3,1fr);gap:20px;
        }
        @media(max-width:768px){.gp-stats-grid{grid-template-columns:repeat(2,1fr);}}
        .gp-stat {
          background:rgba(255,255,255,0.68);
          backdrop-filter:blur(16px) saturate(1.5);
          -webkit-backdrop-filter:blur(16px) saturate(1.5);
          border:1px solid rgba(255,255,255,0.88);
          border-radius:24px;padding:28px 20px;text-align:center;
          box-shadow:0 4px 24px rgba(58,143,163,0.10),inset 0 1px 0 rgba(255,255,255,0.9);
          transition:all 0.35s cubic-bezier(0.4,0,0.2,1);
          opacity:0;transform:translateY(32px);
          position:relative;overflow:hidden;
        }
        .gp-stat::before {
          content:'';position:absolute;top:0;left:20%;right:20%;height:2px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,transparent);
        }
        .gp-stat.in { opacity:1;transform:translateY(0); }
        .gp-stat:hover {
          transform:translateY(-6px);
          box-shadow:0 16px 48px rgba(58,143,163,0.2);
          border-color:rgba(58,143,163,0.28);
        }
        .gp-stat-icon {
          width:44px;height:44px;border-radius:14px;margin:0 auto 14px;
          background:linear-gradient(135deg,rgba(58,143,163,0.12),rgba(90,171,154,0.12));
          border:1px solid rgba(58,143,163,0.18);
          display:flex;align-items:center;justify-content:center;
        }
        .gp-stat-icon svg { width:20px;height:20px;color:#3a8fa3; }
        .gp-stat-val {
          font-family:'Playfair Display',serif;
          font-size:2.4rem;font-weight:800;color:#1a3a4a;line-height:1;margin-bottom:8px;
        }
        .gp-stat-lbl { font-size:0.85rem;color:#5a7d8a;font-weight:500; }

        /* ══ MAP SECTION ══ */
        .gp-map-section { padding:80px 0; }
        .gp-map-header { text-align:center;margin-bottom:44px;opacity:0;transform:translateY(28px);transition:all 0.75s cubic-bezier(0.4,0,0.2,1); }
        .gp-map-header.in { opacity:1;transform:translateY(0); }
        .gp-section-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(1.8rem,3vw,2.6rem);font-weight:800;color:#1a3a4a;margin:0 0 10px;
        }
        .gp-section-sub { font-size:1rem;color:#5a7d8a;max-width:560px;margin:0 auto;line-height:1.7; }

        /* Globe wrapper */
        .gp-globe-wrap {
          position:relative;border-radius:32px;overflow:hidden;
          background:radial-gradient(ellipse at center, #0d2233 0%, #060e18 100%);
          border:1px solid rgba(58,143,163,0.25);
          box-shadow:0 24px 64px rgba(10,30,46,0.5), 0 0 0 1px rgba(58,143,163,0.08),
                     inset 0 1px 0 rgba(90,171,154,0.15);
          opacity:0;transform:translateY(32px);transition:all 0.9s cubic-bezier(0.4,0,0.2,1);
        }
        .gp-globe-wrap.in { opacity:1;transform:translateY(0); }
        .gp-globe-wrap::before {
          content:'';position:absolute;top:0;left:0;right:0;height:3px;z-index:2;pointer-events:none;
          background:linear-gradient(90deg,transparent,#3a8fa3 30%,#5aab9a 50%,#3a8fa3 70%,transparent);
        }
        /* Starfield subtle dots */
        .gp-globe-wrap::after {
          content:'';position:absolute;inset:0;pointer-events:none;z-index:0;
          background-image:
            radial-gradient(circle,rgba(255,255,255,0.55) 1px,transparent 1px),
            radial-gradient(circle,rgba(255,255,255,0.35) 1px,transparent 1px);
          background-size:120px 120px, 80px 80px;
          background-position:0 0, 40px 40px;
          opacity:0.25;
        }
        .gp-globe-inner {
          display:flex;flex-direction:column;align-items:center;
          position:relative;z-index:1;
          padding:32px 0 0;
        }
        .gp-globe-hint {
          font-size:0.72rem;color:rgba(255,255,255,0.3);letter-spacing:0.08em;
          padding:10px 24px 16px;text-align:center;
        }

        /* Map legend on dark */
        .gp-map-legend {
          display:flex;align-items:center;gap:22px;flex-wrap:wrap;
          padding:14px 24px;
          background:rgba(10,30,46,0.7);
          border-top:1px solid rgba(58,143,163,0.15);
        }
        .gp-legend-item { display:flex;align-items:center;gap:7px;font-size:0.74rem;color:rgba(255,255,255,0.45);font-family:'Outfit',sans-serif; }
        .gp-legend-dot  { width:8px;height:8px;border-radius:50%;flex-shrink:0; }

        /* ── TOOLTIP — rich detail card ── */
        .gp-tooltip {
          position:absolute;pointer-events:none;z-index:50;
          width:260px;border-radius:20px;overflow:hidden;
          transform:translate(-50%, calc(-100% - 22px));
          box-shadow:0 28px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(90,171,154,0.25);
          animation:gpTTIn 0.2s cubic-bezier(0.4,0,0.2,1) both;
        }
        @keyframes gpTTIn {
          from { opacity:0; transform:translate(-50%, calc(-100% - 10px)) scale(0.93); }
          to   { opacity:1; transform:translate(-50%, calc(-100% - 22px)) scale(1); }
        }

        /* Header */
        .gp-tt-head {
          padding:16px 18px 12px;
          background:linear-gradient(135deg,#0a1e2e 0%,#1a3a4a 100%);
          border-bottom:1px solid rgba(58,143,163,0.2);
          position:relative;
        }
        .gp-tt-head::before {
          content:'';position:absolute;top:0;left:0;right:0;height:2px;
          background:linear-gradient(90deg,transparent,#5aab9a,#3a8fa3,transparent);
        }
        .gp-tt-head-grid {
          position:absolute;inset:0;pointer-events:none;
          background-image:linear-gradient(rgba(58,143,163,0.05) 1px,transparent 1px),
                           linear-gradient(90deg,rgba(58,143,163,0.05) 1px,transparent 1px);
          background-size:18px 18px;
        }
        .gp-tt-flag-row {
          display:flex;align-items:center;gap:10px;margin-bottom:6px;
          position:relative;z-index:1;
        }
        .gp-tt-flag { font-size:1.6rem;line-height:1; }
        .gp-tt-name-col { flex:1; }
        .gp-tt-name {
          font-size:1rem;font-weight:700;color:#fff;
          font-family:'Outfit',sans-serif;line-height:1.2;
        }
        .gp-tt-capital {
          font-size:0.68rem;color:rgba(255,255,255,0.4);
          margin-top:2px;letter-spacing:0.04em;
        }
        .gp-tt-badge-hq {
          font-size:0.56rem;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;
          padding:3px 9px;border-radius:999px;
          background:rgba(90,171,154,0.2);color:#5aab9a;border:1px solid rgba(90,171,154,0.35);
          white-space:nowrap;
        }
        .gp-tt-badge-exp {
          font-size:0.56rem;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;
          padding:3px 9px;border-radius:999px;
          background:rgba(58,143,163,0.15);color:rgba(200,235,240,0.7);border:1px solid rgba(58,143,163,0.25);
          white-space:nowrap;
        }
        .gp-tt-region {
          font-size:0.68rem;color:rgba(255,255,255,0.3);
          letter-spacing:0.08em;text-transform:uppercase;
          position:relative;z-index:1;margin-top:2px;
        }

        /* Info rows */
        .gp-tt-body { padding:12px 18px 14px;background:#fff; }
        .gp-tt-info-grid {
          display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;
        }
        .gp-tt-info-item {}
        .gp-tt-info-lbl {
          font-size:0.58rem;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;
          color:#9ab0b8;margin-bottom:2px;
        }
        .gp-tt-info-val {
          font-size:0.78rem;font-weight:600;color:#1a3a4a;
        }
        .gp-tt-status-active  { color:#22a06b; }
        .gp-tt-status-growing { color:#e67e22; }

        /* Divider */
        .gp-tt-divider {
          height:1px;background:rgba(58,143,163,0.12);margin-bottom:10px;
        }

        /* Products */
        .gp-tt-lbl  { font-size:0.6rem;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#9ab0b8;margin-bottom:7px; }
        .gp-tt-tags { display:flex;flex-wrap:wrap;gap:5px; }
        .gp-tt-tag  {
          font-size:0.65rem;font-weight:600;padding:3px 10px;border-radius:999px;
          background:rgba(58,143,163,0.08);color:#3a8fa3;border:1px solid rgba(58,143,163,0.2);
        }

        /* Arrow pointer */
        .gp-tt-arrow {
          position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);
          width:16px;height:8px;overflow:hidden;
        }
        .gp-tt-arrow::before {
          content:'';position:absolute;top:-8px;left:50%;transform:translateX(-50%) rotate(45deg);
          width:14px;height:14px;background:#fff;
          box-shadow:0 4px 12px rgba(0,0,0,0.15);
        }

        /* ══ COUNTRY LIST ══ */
        .gp-list { padding:80px 0; }
        .gp-list-grid {
          display:grid;grid-template-columns:repeat(3,1fr);gap:18px;
        }
        @media(max-width:900px){.gp-list-grid{grid-template-columns:repeat(2,1fr);}}
        @media(max-width:580px){.gp-list-grid{grid-template-columns:1fr;}}

        .gp-country-card {
          background:rgba(255,255,255,0.65);
          backdrop-filter:blur(16px) saturate(1.4);
          -webkit-backdrop-filter:blur(16px) saturate(1.4);
          border:1px solid rgba(255,255,255,0.88);
          border-radius:18px;padding:18px 20px;
          box-shadow:0 2px 12px rgba(0,0,0,0.05),inset 0 1px 0 rgba(255,255,255,0.9);
          display:flex;align-items:flex-start;gap:14px;
          transition:all 0.3s cubic-bezier(0.4,0,0.2,1);
          opacity:0;transform:translateY(24px);
          position:relative;overflow:hidden;
        }
        .gp-country-card::before {
          content:'';position:absolute;left:0;top:0;bottom:0;width:3px;
          background:linear-gradient(180deg,#3a8fa3,#5aab9a);
          opacity:0;transition:opacity 0.3s;
        }
        .gp-country-card.in { opacity:1;transform:translateY(0); }
        .gp-country-card:hover {
          background:rgba(255,255,255,0.92);
          box-shadow:0 8px 28px rgba(58,143,163,0.16);
          border-color:rgba(58,143,163,0.22);
          transform:translateX(6px);
        }
        .gp-country-card:hover::before { opacity:1; }
        .gp-country-icon {
          width:38px;height:38px;border-radius:12px;flex-shrink:0;
          display:flex;align-items:center;justify-content:center;
          background:linear-gradient(135deg,rgba(58,143,163,0.1),rgba(90,171,154,0.1));
          border:1px solid rgba(58,143,163,0.18);
        }
        .gp-country-icon svg { width:17px;height:17px;color:#3a8fa3; }
        .gp-country-name { font-size:0.95rem;font-weight:700;color:#1a3a4a;margin-bottom:3px; }
        .gp-country-region { font-size:0.75rem;color:#5a7d8a;margin-bottom:6px; }
        .gp-country-tags { display:flex;flex-wrap:wrap;gap:4px; }
        .gp-country-tag {
          font-size:0.65rem;font-weight:600;padding:2px 8px;border-radius:999px;
          background:rgba(58,143,163,0.07);color:#3a8fa3;
          border:1px solid rgba(58,143,163,0.15);
        }
        .gp-country-hq {
          font-size:0.6rem;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;
          padding:2px 8px;border-radius:999px;
          background:rgba(58,143,163,0.15);color:#3a8fa3;
          border:1px solid rgba(58,143,163,0.25);
        }

        /* ══ CTA BANNER ══ */
        .gp-cta-wrap {
          padding:60px 32px 80px;
          background:linear-gradient(180deg,#c8e8ed 0%,#e0f4f7 100%);
        }
        .gp-cta {
          max-width:1200px;margin:0 auto;border-radius:32px;overflow:hidden;
          position:relative;
          background:linear-gradient(135deg,#0d2233 0%,#1a3a4a 60%,#0f2d3d 100%);
          display:grid;grid-template-columns:1.2fr 0.8fr;min-height:280px;
          opacity:0;transform:translateY(36px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        @media(max-width:768px){.gp-cta{grid-template-columns:1fr;}}
        .gp-cta.in { opacity:1;transform:translateY(0); }
        .gp-cta-gridlines {
          position:absolute;inset:0;pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:48px 48px;
        }
        .gp-cta-topline {
          position:absolute;top:0;left:0;right:0;height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,#3a8fa3,transparent);
        }
        .gp-cta-left { position:relative;z-index:2;padding:52px 48px; }
        .gp-cta-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(1.6rem,3vw,2.2rem);font-weight:800;
          color:#fff;margin:0 0 20px;line-height:1.25;
        }
        .gp-cta-body { font-size:0.95rem;color:rgba(255,255,255,0.65);line-height:1.85;margin-bottom:16px; }
        .gp-cta-right {
          position:relative;z-index:2;display:flex;
          align-items:center;justify-content:center;padding:40px;
        }
        .gp-cta-globe {
          font-size:12rem;font-weight:900;
          background:linear-gradient(135deg,rgba(58,143,163,0.4),rgba(90,171,154,0.25));
          -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
          filter:drop-shadow(0 0 40px rgba(58,143,163,0.45));
          animation:gpFloat 4s ease-in-out infinite;
          user-select:none;
        }
        .gp-cta-orb1 { position:absolute;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(58,143,163,0.18) 0%,transparent 70%);top:-60px;right:-60px;pointer-events:none; }
        .gp-cta-orb2 { position:absolute;width:200px;height:200px;border-radius:50%;background:radial-gradient(circle,rgba(90,171,154,0.15) 0%,transparent 70%);bottom:-40px;left:-40px;pointer-events:none; }
      `}</style>

      {/* ══ HERO ══ */}
      <section className="gp-hero">
        <img src={qualityImage} alt="" className="gp-hero-bg" />
        <div className="gp-hero-gridlines" />
        <div className="gp-hero-topline" />
        <div className="gp-hero-inner">
          <div className="gp-hero-anim">
            {/* <div className="gp-hero-eyebrow">{t('globalPage.hero.eyebrow')}</div> */}
            <h1 className="gp-hero-title">{t('globalPage.hero.title')}</h1>
            <div className="gp-hero-sub">{t('globalPage.hero.sub')}</div>
          </div>
        </div>
      </section>

      {/* ══ STATS ══ */}
      <section className="gp-teal gp-stats">
        <div className="gp-dots" /><div className="gp-blob gp-blob-br" />
        <div className="gp-inner">
          <div ref={statsRef} className="gp-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className={`gp-stat ${statsVis ? 'in' : ''}`}
                style={{ transitionDelay: `${i * 110}ms` }}>
                <div className="gp-stat-icon"><s.icon /></div>
                <div className="gp-stat-val"><CountUp end={s.val} />{s.suffix}</div>
                <div className="gp-stat-lbl">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MAP SECTION ══ */}
      <section className="gp-teal-2 gp-map-section">
        <div className="gp-dots" />
        <div className="gp-blob gp-blob-tl" />

        <div className="gp-inner">

          <div ref={mapSecRef} className={`gp-map-header ${mapSecVis ? 'in' : ''}`}>
            <h2 className="gp-section-title">{t('globalPage.map.title')}</h2>
            <p className="gp-section-sub">{t('globalPage.map.subtitle')}</p>
          </div>

          <div
            ref={wrapRef}
            className={`gp-globe-wrap ${mapSecVis ? 'in' : ''}`}
            onMouseMove={handleGlobeMouseMove}
          >
            <div className="gp-globe-inner">
              <GlobeGL
                ref={globeRef}
                width={typeof window !== 'undefined' ? Math.min(window.innerWidth - 80, 900) : 800}
                height={500}
                backgroundColor="rgba(0,0,0,0)"

                /* Earth texture */
                globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
                bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
                atmosphereColor="#3a8fa3"
                atmosphereAltitude={0.18}

                /* Arc lines HQ → countries */
                arcsData={arcs}
                arcStartLat="startLat"
                arcStartLng="startLng"
                arcEndLat="endLat"
                arcEndLng="endLng"
                arcColor={(d) => [d.color + 'aa', d.color + 'ff']}
                arcAltitude={0.25}
                arcStroke={1.2}
                arcDashLength={0.4}
                arcDashGap={0.15}
                arcDashAnimateTime={2200}

                /* Points */
                pointsData={countries}
                pointLat="lat"
                pointLng="lng"
                pointColor={(d) => d.type === 'HQ' ? '#ffffff' : (regionColors[d.region] || '#3a8fa3')}
                pointAltitude={0.02}
                pointRadius={(d) => d.type === 'HQ' ? 1.0 : 0.7}
                pointLabel={() => ''}

                /* Hover — debounced to prevent 1-frame flicker */
                onPointHover={handlePointHover}
              />

              <div className="gp-globe-hint">
                🖱 Drag to rotate · Scroll to zoom · Hover markers for details
              </div>
            </div>

            {/* Legend */}
            <div className="gp-map-legend">
              <div className="gp-legend-item">
                <div className="gp-legend-dot" style={{ background:'#fff', border:'2px solid #3a8fa3', boxSizing:'border-box' }}/>
                <span>Headquarters</span>
              </div>
              {Object.entries(regionColors)
                .filter(([k]) => !['América','Europa','África','Medio Oriente','Oceanía'].includes(k))
                .map(([region, color]) => (
                  <div key={region} className="gp-legend-item">
                    <div className="gp-legend-dot" style={{ background: color }}/>
                    <span>{region}</span>
                  </div>
                ))
              }
            </div>

            {/* Rich Tooltip */}
            {activeCountry && (
              <div className="gp-tooltip" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
                {/* Header */}
                <div className="gp-tt-head">
                  <div className="gp-tt-head-grid"/>
                  <div className="gp-tt-flag-row">
                    <span className="gp-tt-flag"><img
                      src={activeCountry.flag}
                      alt={activeCountry.name}
                      style={{
                        width: "32px",
                        height: "22px",
                        objectFit: "cover",
                        marginLeft: "auto",
                        flexShrink: 0
                      }}
                    /></span>
                    <div className="gp-tt-name-col">
                      <div className="gp-tt-name">{activeCountry.name}</div>
                      {activeCountry.capital && (
                        <div className="gp-tt-capital">📍 {activeCountry.capital}</div>
                      )}
                    </div>
                    {activeCountry.type === 'HQ'
                      ? <span className="gp-tt-badge-hq">HQ</span>
                      : <span className="gp-tt-badge-exp">Export</span>
                    }
                  </div>
                  <div className="gp-tt-region">{activeCountry.region}</div>
                </div>

                {/* Info grid */}
                {/* <div className="gp-tt-body">
                  <div className="gp-tt-info-grid">
                    <div className="gp-tt-info-item">
                      <div className="gp-tt-info-lbl">Partnership</div>
                      <div className="gp-tt-info-val">{activeCountry.partnership || '—'}</div>
                    </div>
                    <div className="gp-tt-info-item">
                      <div className="gp-tt-info-lbl">Since</div>
                      <div className="gp-tt-info-val">{activeCountry.since || '—'}</div>
                    </div>
                    <div className="gp-tt-info-item">
                      <div className="gp-tt-info-lbl">Focus Area</div>
                      <div className="gp-tt-info-val">{activeCountry.focus || '—'}</div>
                    </div>
                    <div className="gp-tt-info-item">
                      <div className="gp-tt-info-lbl">Status</div>
                      <div className={`gp-tt-info-val gp-tt-status-${(activeCountry.status || '').toLowerCase()}`}>
                        {activeCountry.status === 'Active' ? '● Active' : activeCountry.status === 'Growing' ? '▲ Growing' : activeCountry.status || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="gp-tt-divider"/>

                  <div className="gp-tt-lbl">Products</div>
                  <div className="gp-tt-tags">
                    {(activeCountry.products || []).map((p, j) => (
                      <span key={j} className="gp-tt-tag">{p}</span>
                    ))}
                  </div>
                </div> */}

                {/* Arrow */}
                <div className="gp-tt-arrow"/>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* ══ COUNTRY LIST ══ */}
       <section className="gp-teal gp-list">
        <div className="gp-dots" /><div className="gp-blob gp-blob-br" />
        <div className="gp-inner">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 className="gp-section-title">{t('globalPage.map.title')}</h2>
            <p className="gp-section-sub" style={{ marginTop: '10px' }}>
              {t('globalPage.map.subtitle')}
            </p>
          </div>
          <div ref={listRef} className="gp-list-grid">
            {countries.map((country, i) => (
              <div key={i} className={`gp-country-card ${listVis ? 'in' : ''}`}
                style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="gp-country-icon"><MapPin /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                    <div className="gp-country-name">{country.name}</div>
                    {country.type === 'HQ' && <span className="gp-country-hq">HQ</span>}
                  </div>
                  <div className="gp-country-region">{country.region}</div>
                  {/* <div className="gp-country-tags">
                    {country.products.map((p, j) => (
                      <span key={j} className="gp-country-tag">{p}</span>
                    ))}
                  </div> */}
                </div>
                {countryDetails[country.name]?.flag && (
  <img
    src={countryDetails[country.name].flag}
    alt={country.name}
    style={{
      width: "32px",
      height: "22px",
      objectFit: "cover",
      marginLeft: "auto",
      flexShrink: 0
    }}
  />
)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA BANNER ══ */}
      <div className="gp-cta-wrap">
        <div ref={ctaRef} className={`gp-cta ${ctaVis ? 'in' : ''}`}>
          <div className="gp-cta-gridlines" />
          <div className="gp-cta-topline" />
          <div className="gp-cta-orb1" /><div className="gp-cta-orb2" />
          <div className="gp-cta-left">
            <h2 className="gp-cta-title">{t('globalPage.cta.title')}</h2>
            <p className="gp-cta-body">{t('globalPage.cta.p1')}</p>
            <p className="gp-cta-body">{t('globalPage.cta.p2')}</p>
          </div>
          <div className="gp-cta-right">
            <div className="gp-cta-globe">🌐</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalPresence;